import { ArrowLeft, ArrowRight } from "lucide-react";
import { cn } from "@/shared/lib/cn";
import { Button } from "../core/Button/Button";
import { SubmitButton } from "../fields/SubmitButton";

export interface WizardNavProps {
  /**
   * Qué hace "Atrás". Si se omite, el botón no se pinta — que es lo que
   * corresponde en el primer paso: un botón deshabilitado ocuparía sitio
   * para no hacer nada y el foco pasaría por él.
   */
  onBack?: () => void;
  backLabel?: string;
  /** Qué hace "Siguiente". No se pinta en el último paso. */
  onNext?: () => void;
  nextLabel?: string;
  /**
   * Es el último paso: en lugar de "Siguiente" se pinta el botón que ENVÍA
   * el formulario.
   */
  isLast?: boolean;
  /** Texto del botón final. */
  submitLabel?: string;
  /**
   * Fuerza el estado de envío. Por omisión lo saca del formulario que lo
   * envuelve, igual que `SubmitButton`.
   */
  loading?: boolean;
  className?: string;
}

/**
 * La botonera de un wizard: Atrás a la izquierda, la acción que empuja
 * hacia adelante a la derecha.
 *
 * ── Por qué el botón final es un `submit` de verdad ─────────────────
 * Podría ser un `<button type="button">` con un `onClick` que llame al
 * envío, y entonces esta pieza no dependería del formulario. Pero un
 * formulario cuyo botón final no es `type="submit"` deja de responder a
 * Enter y deja de anunciarse como el envío del formulario. Se prefiere que
 * `WizardNav` viva dentro de un `<Form>` —que es donde vivirán todos los
 * wizards de este sistema— a perder eso.
 *
 * ── Por qué "Siguiente" es `type="button"` ──────────────────────────
 * Porque en los pasos intermedios NO hay nada que enviar. Si fuera un
 * submit, avanzar validaría el formulario entero y sacaría los errores de
 * campos que la persona todavía no ha visto. Avanzar es un movimiento de
 * la interfaz; enviar ocurre una sola vez, al final.
 */
export function WizardNav({
  onBack,
  backLabel = "Atrás",
  onNext,
  nextLabel = "Siguiente",
  isLast = false,
  submitLabel = "Continuar",
  loading,
  className,
}: WizardNavProps) {
  return (
    <div className={cn("mt-1 flex items-center gap-3", className)}>
      {onBack && (
        <Button
          variant="ghost"
          size="lg"
          shape="rounded"
          leftIcon={<ArrowLeft />}
          onClick={onBack}
          disabled={loading}
        >
          {backLabel}
        </Button>
      )}

      {isLast ? (
        <SubmitButton
          size="lg"
          shape="rounded"
          fullWidth
          loading={loading}
          // `SubmitButton` trae `mt-1` para separarse del último campo. Aquí
          // el separador ya lo pone la fila, y ese margen extra dejaría el
          // botón un píxel más abajo que "Atrás".
          className="mt-0"
        >
          {submitLabel}
        </SubmitButton>
      ) : (
        <Button
          type="button"
          variant="primary"
          size="lg"
          shape="rounded"
          fullWidth
          rightIcon={<ArrowRight />}
          onClick={onNext}
          disabled={loading}
        >
          {nextLabel}
        </Button>
      )}
    </div>
  );
}
